import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react'

// React 17 has no useId; this counter is process-local and only used to
// generate stable list ids for aria-controls/aria-activedescendant wiring.
let comboboxCounter = 0
function generateListId(): string {
  comboboxCounter += 1
  return `deal-combobox-${comboboxCounter}`
}

export type ComboboxOption = string | { value: string; label: string }

interface NormalizedOption {
  value: string
  label: string
}

function normalizeOption(option: ComboboxOption): NormalizedOption {
  return typeof option === 'string' ? { value: option, label: option } : option
}

interface ComboboxProps {
  value: string
  onChange: (next: string) => void
  options: readonly ComboboxOption[]
  placeholder?: string
  disabled?: boolean
  required?: boolean
  id?: string
  'aria-label'?: string
}

export const Combobox: React.FunctionComponent<ComboboxProps> = (props) => {
  const {
    value,
    onChange,
    options,
    placeholder,
    disabled = false,
    required = false,
    id,
  } = props
  const ariaLabel = props['aria-label']

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlight, setHighlight] = useState<number | null>(null)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const listIdRef = useRef<string | null>(null)
  if (listIdRef.current === null) listIdRef.current = generateListId()
  const listId = listIdRef.current

  const normalizedOptions = useMemo(
    () => options.map((option) => normalizeOption(option)),
    [options],
  )

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim()
    if (!q) return normalizedOptions.slice()
    return normalizedOptions.filter((option) => option.label.toLowerCase().includes(q))
  }, [normalizedOptions, query])

  useEffect(() => {
    if (!open) return undefined
    const handler = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false)
        setQuery('')
        setHighlight(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  useEffect(() => {
    if (!open) {
      setHighlight(null)
      return
    }
    setHighlight(filtered.length > 0 ? 0 : null)
  }, [open, query, filtered.length])

  function commit(option: NormalizedOption): void {
    onChange(option.value)
    setOpen(false)
    setQuery('')
    setHighlight(null)
  }

  function openMenu(): void {
    if (disabled) return
    setOpen(true)
    setQuery('')
  }

  function toggle(): void {
    if (disabled) return
    if (open) {
      setOpen(false)
      setQuery('')
      setHighlight(null)
    } else {
      openMenu()
      inputRef.current?.focus()
    }
  }

  function handleKey(event: ReactKeyboardEvent<HTMLInputElement>): void {
    if (disabled) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (!open) {
        openMenu()
        return
      }
      if (filtered.length === 0) return
      setHighlight((current) =>
        current === null || current >= filtered.length - 1 ? 0 : current + 1,
      )
    } else if (event.key === 'ArrowUp') {
      if (!open) return
      event.preventDefault()
      if (filtered.length === 0) return
      setHighlight((current) =>
        current === null || current <= 0 ? filtered.length - 1 : current - 1,
      )
    } else if (event.key === 'Enter') {
      if (!open) return
      if (highlight !== null && filtered[highlight] !== undefined) {
        event.preventDefault()
        event.stopPropagation()
        commit(filtered[highlight])
      } else {
        event.preventDefault()
      }
    } else if (event.key === 'Escape') {
      if (!open) return
      event.preventDefault()
      event.stopPropagation()
      setOpen(false)
      setQuery('')
      setHighlight(null)
    } else if (event.key === 'Tab') {
      if (!open) return
      setOpen(false)
      setQuery('')
      setHighlight(null)
    }
  }

  const displayValue = open
    ? query
    : normalizedOptions.find((option) => option.value === value)?.label ?? value
  const wrapClass = `combobox-wrap${disabled ? ' combobox-wrap--disabled' : ''}`

  return (
    <div ref={wrapRef} className={wrapClass} data-open={open ? '' : undefined}>
      <input
        ref={inputRef}
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-disabled={disabled || undefined}
        aria-activedescendant={
          open && highlight !== null ? `${listId}-opt-${highlight}` : undefined
        }
        className="combobox-input form-input"
        value={displayValue}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        aria-label={ariaLabel}
        autoComplete="off"
        onChange={(event) => {
          setQuery(event.target.value)
          if (!open) setOpen(true)
        }}
        onFocus={() => {
          if (!open && !disabled) openMenu()
        }}
        onKeyDown={handleKey}
      />
      <button
        type="button"
        className="combobox-caret"
        tabIndex={-1}
        disabled={disabled}
        aria-label={open ? 'Close menu' : 'Open menu'}
        onMouseDown={(event) => event.preventDefault()}
        onClick={toggle}
      >
        ▾
      </button>
      {open && (
        <ul id={listId} role="listbox" className="combobox-menu">
          {filtered.length > 0 ? (
            filtered.map((option, index) => (
              <li
                key={option.value}
                id={`${listId}-opt-${index}`}
                role="option"
                aria-selected={index === highlight}
                className={`combobox-item${index === highlight ? ' active' : ''}`}
                onMouseDown={(event) => {
                  event.preventDefault()
                  commit(option)
                }}
                onMouseEnter={() => setHighlight(index)}
              >
                {option.label}
              </li>
            ))
          ) : (
            <li className="combobox-empty">No matches</li>
          )}
        </ul>
      )}
    </div>
  )
}
